import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Loader2, Check, MapPin, Phone, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Footer } from '../components/FooterCTA';
import useReveal from '../hooks/useReveal';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '../i18n/I18nProvider';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ nom: '', prenom: '', restaurant_name: '', telephone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useReveal();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.telephone) {
      toast.error(t('contact.toastRequired'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/leads/contact`, form);
      setDone(true);
      toast.success(t('contact.toastSuccess'));
    } catch {
      toast.error(t('contact.toastError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-24 pb-20">
        <section className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <p className="text-sm font-semibold text-[#76B828]">{t('contact.eyebrow')}</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 text-[#0b0b0f]">{t('contact.title')}</h1>
            <p className="text-[#55555f] mt-4 text-lg">{t('contact.subtitle')}</p>
          </div>

          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 mt-16">
            <div className="reveal">
              <div className="bg-[#0b0b0f] rounded-3xl p-8 md:p-10 text-white">
                <h2 className="font-display text-2xl">Bookers</h2>
                <p className="text-white/60 mt-3 leading-relaxed text-[15px]">{t('contact.blurb')}</p>

                <div className="mt-10 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                      <MapPin size={18} />
                    </div>
                    <div>
                      <p className="font-semibold text-[15px]">{t('contact.address')}</p>
                      <p className="text-white/55 text-[14px] mt-1">{t('contact.addressValue')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                      <Phone size={18} />
                    </div>
                    <div>
                      <p className="font-semibold text-[15px]">{t('contact.phone')}</p>
                      <p className="text-white/55 text-[14px] mt-1">+41 00 000 00 00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 shrink-0 rounded-xl bg-white/10 flex items-center justify-center">
                      <Mail size={18} />
                    </div>
                    <div>
                      <p className="font-semibold text-[15px]">{t('contact.email')}</p>
                      <p className="text-white/55 text-[14px] mt-1">hello@wearebookers.com</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="text-sm text-white/40">{t('contact.supportHours')}</p>
                  <p className="text-white/70 text-[15px] mt-1">{t('contact.supportLine')}</p>
                </div>
              </div>
            </div>

            <div className="reveal" style={{ transitionDelay: '100ms' }}>
              {!done ? (
                <form onSubmit={submit} className="bg-white rounded-3xl border border-black/5 shadow-sm p-8 md:p-10" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('contact.lastName')} *</label>
                      <input value={form.nom} onChange={update('nom')} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('contact.placeholderLast')} data-testid="contact-nom" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('contact.firstName')} *</label>
                      <input value={form.prenom} onChange={update('prenom')} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('contact.placeholderFirst')} data-testid="contact-prenom" />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className="text-sm font-medium text-[#3a3a42]">{t('contact.restaurant')}</label>
                    <input value={form.restaurant_name} onChange={update('restaurant_name')} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('contact.placeholderRestaurant')} data-testid="contact-restaurant" />
                  </div>
                  <div className="mt-5">
                    <label className="text-sm font-medium text-[#3a3a42]">{t('contact.phone')} *</label>
                    <input value={form.telephone} onChange={update('telephone')} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('contact.placeholderPhone')} data-testid="contact-telephone" />
                  </div>
                  <div className="mt-5">
                    <label className="text-sm font-medium text-[#3a3a42]">{t('contact.message')}</label>
                    <textarea value={form.message} onChange={update('message')} rows={4} className="mt-1.5 w-full rounded-xl border border-black/10 px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition resize-none" placeholder={t('contact.placeholderMessage')} data-testid="contact-message" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-pill btn-dark w-full py-3.5 text-[15px] mt-6 disabled:opacity-70" data-testid="contact-submit">
                    {loading ? (<><Loader2 size={18} className="animate-spin" /> {t('contact.sending')}</>) : (<><Send size={17} /> {t('contact.submit')}</>)}
                  </button>
                </form>
              ) : (
                <div className="bg-white rounded-3xl border border-black/5 shadow-sm p-8 md:p-10 text-center py-20" data-testid="contact-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#e9f9f0] flex items-center justify-center">
                    <Check size={30} className="text-[#16a34a]" />
                  </div>
                  <h3 className="font-display text-2xl mt-5 text-[#0b0b0f]">{t('contact.successTitle')}</h3>
                  <p className="text-[#6b6b73] mt-2 max-w-sm mx-auto">{t('contact.successBody', '', { name: form.prenom || '' })}</p>
                  <Link to="/" className="btn-pill btn-dark px-8 py-3 mt-6 inline-flex">{t('contact.backHome')}</Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
